import React, { useEffect, useMemo, useState } from 'react';
import http from '../api/http';
import { useToasts } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import SimplePdfViewer from '../components/SimplePdfViewer';
import { openWithAuth } from '../utils/authedResource';

const cardStyle = {
  flex: 1,
  minWidth: '200px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  padding: '1.25rem 1.5rem',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem'
};

const EmployeeOverviewPage = () => {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewRecord, setPreviewRecord] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailType, setDetailType] = useState(null); // employees | edu_l3 | edu_prejob | edu_daily | attachments
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // 批量 PDF 导入本地状态（复用档案页面逻辑）
  const [importing, setImporting] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [archiveTypeImport, setArchiveTypeImport] = useState('三级教育档案');

  useEffect(() => {
    const load = async () => {
      console.log('档案概览: 开始加载数据...');
      setLoading(true); setError(null);
      try {
        const resp = await http.get('/employees/overview');
        console.log('档案概览: 数据加载成功', resp.data);
        setData(resp.data);
      } catch (e) {
        const msg = e.response?.data?.error || '获取档案概览失败';
        console.error('档案概览: 数据加载失败', e);
        setError(msg);
        addToast(msg, 'error');
      } finally {
        console.log('档案概览: 加载流程结束');
        setLoading(false);
      }
    };
    load();
  }, [addToast]);

  useEffect(() => {
    if (!previewRecord) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setPreviewRecord(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [previewRecord]);

  useEffect(() => {
    if (!detailOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setDetailOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [detailOpen]);

  const openPdfPreview = (item) => {
    if (!item?.file_url) return;
    setPreviewRecord({
      title: item.title || item.file_name || item.archive_desc || 'PDF 预览',
      file_url: item.file_url
    });
  };

  const detailTitle = useMemo(() => {
    switch (detailType) {
      case 'employees':
        return '员工列表';
      case 'edu_l3':
        return '三级教育记录';
      case 'edu_prejob':
        return '岗前教育记录';
      case 'edu_daily':
        return '日常教育记录';
      case 'attachments':
        return '附件列表';
      default:
        return '列表';
    }
  }, [detailType]);

  const fetchDetails = async (type) => {
    setDetailLoading(true);
    setDetailRows([]);
    try {
      if (type === 'employees') {
        const resp = await http.get('/employees/overview-details/employees', { params: { limit: 500 } });
        setDetailRows(Array.isArray(resp.data) ? resp.data : []);
        return;
      }
      if (type === 'edu_l3') {
        const resp = await http.get('/employees/overview-details/education', { params: { type: '三级教育', limit: 500 } });
        setDetailRows(Array.isArray(resp.data) ? resp.data : []);
        return;
      }
      if (type === 'edu_prejob') {
        const resp = await http.get('/employees/overview-details/education', { params: { type: '岗前教育', limit: 500 } });
        setDetailRows(Array.isArray(resp.data) ? resp.data : []);
        return;
      }
      if (type === 'edu_daily') {
        const resp = await http.get('/employees/overview-details/education', { params: { type: '日常教育', limit: 500 } });
        setDetailRows(Array.isArray(resp.data) ? resp.data : []);
        return;
      }
      if (type === 'attachments') {
        const resp = await http.get('/employees/overview-details/attachments', { params: { limit: 500 } });
        setDetailRows(Array.isArray(resp.data) ? resp.data : []);
        return;
      }
      setDetailRows([]);
    } catch (e) {
      const msg = e.response?.data?.error || '加载列表失败';
      addToast(msg, 'error');
      setDetailRows([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetails = async (type) => {
    setDetailType(type);
    setDetailOpen(true);
    await fetchDetails(type);
  };

  const statCards = data && [
    { label: '员工总数', value: data.employees, type: 'employees' },
    { label: '三级教育记录', value: data.education['三级教育'], type: 'edu_l3' },
    { label: '岗前教育记录', value: data.education['岗前教育'], type: 'edu_prejob' },
    { label: '日常教育记录', value: data.education['日常教育'], type: 'edu_daily' },
    { label: '附件总数', value: data.attachments, type: 'attachments' }
  ];

  return (
    <div style={{ padding: '0 0.5rem' }}>
      <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '1.5rem' }}>档案概览</h1>
      <div style={{ background:'#fff', padding:'1rem 1.25rem', border:'1px solid var(--border-color)', borderRadius:'10px', marginBottom:'1.25rem' }}>
        <h2 style={{ fontSize:'1.25rem', margin:'0 0 .75rem 0' }}>批量导入 PDF 档案</h2>
        <p style={{ fontSize:'.8rem', color:'var(--text-secondary)', margin:'0 0 .75rem 0' }}>选择多个 PDF，系统将尝试从文件名解析员工姓名并写入档案。文件名示例：张三-入职体检.pdf；李四(三级教育).pdf。</p>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'0.75rem', alignItems:'center', marginBottom:'.75rem' }}>
          <input type="file" multiple accept="application/pdf" onChange={e=>setImportFiles(Array.from(e.target.files||[]))} style={{ flex:'1 1 260px' }} />
          {/* 档案类别下拉：与 EmployeeFilesPage 中 tabs 保持一致 */}
          <select value={archiveTypeImport} onChange={e=>setArchiveTypeImport(e.target.value)} style={{ flex:'0 0 160px', padding:'0.55rem .75rem', border:'1px solid var(--border-color)', borderRadius:'6px', background:'#fff' }}>
            <option value="三级教育档案">三级教育档案</option>
            <option value="岗前教育档案">岗前教育档案</option>
            <option value="日常教育档案">日常教育档案</option>
            <option value="职业健康信息">职业健康信息</option>
            <option value="违章记录">违章记录</option>
          </select>
          <button disabled={importing || importFiles.length===0} onClick={async ()=>{
            setImporting(true); setImportSummary(null);
            try {
              const fd = new FormData(); importFiles.forEach(f=>fd.append('files', f));
              // 使用 axios 封装 http，自动带上鉴权与错误拦截
              const { data } = await http.post(`/archives/import-pdfs?archive_type=${encodeURIComponent(archiveTypeImport)}`, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
              setImportSummary(data);
              if (data.code === 0) { addToast('批量导入完成', 'success'); } else { addToast(data.message || '导入失败', 'error'); }
            } catch (e) {
              if (e.response?.status === 405) {
                addToast('服务器返回 405 (Method Not Allowed)。请确认后端已重启并包含 /api/archives/import-pdfs 路由。', 'error');
              } else {
                addToast(e.response?.data?.message || e.message || '上传失败', 'error');
              }
              setImportSummary({ code: e.response?.status || -1, message: e.response?.data?.message || e.message });
            } finally { setImporting(false); }
          }} style={{ padding:'.6rem 1rem', background: importing?'#94a3b8':'var(--primary-color)', color:'#fff', border:'none', borderRadius:'6px', cursor: importing?'not-allowed':'pointer' }}>{importing?'上传中...':'开始导入'}</button>
        </div>
        {importSummary && (
          <div style={{ background:'var(--bg-secondary)', padding:'.75rem', borderRadius:'6px', fontSize:'.75rem' }}>
            {importSummary.code !== 0 && <div style={{ color:'var(--danger-color)' }}>失败: {importSummary.message}</div>}
            {importSummary.code === 0 && (
              <>
                <div style={{ marginBottom:'.4rem' }}>总文件 {importSummary.total} | 成功存储 {importSummary.stored} | 匹配员工 {importSummary.matched}</div>
                {importSummary.unmatched?.length>0 && (
                  <details style={{ marginBottom:'.35rem' }}>
                    <summary style={{ cursor:'pointer' }}>未匹配 ({importSummary.unmatched.length})</summary>
                    <ul style={{ margin:'.35rem 0', paddingLeft:'1.1rem' }}>
                      {importSummary.unmatched.map((u,i)=><li key={i}>{u.file} - {u.reason}</li>)}
                    </ul>
                  </details>
                )}
                {importSummary.ambiguous?.length>0 && (
                  <details style={{ marginBottom:'.35rem' }}>
                    <summary style={{ cursor:'pointer' }}>重名模糊 ({importSummary.ambiguous.length})</summary>
                    <ul style={{ margin:'.35rem 0', paddingLeft:'1.1rem' }}>
                      {importSummary.ambiguous.map((a,i)=><li key={i}>{a.file} - 匹配 {a.count} 个候选</li>)}
                    </ul>
                  </details>
                )}
                {importSummary.errors?.length>0 && (
                  <details>
                    <summary style={{ cursor:'pointer' }}>错误 ({importSummary.errors.length})</summary>
                    <ul style={{ margin:'.35rem 0', paddingLeft:'1.1rem' }}>
                      {importSummary.errors.map((e,i)=><li key={i}>{e.file} - {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {loading && <p>正在加载概览数据...</p>}
      {error && <p style={{ color: 'var(--danger-color)' }}>{error}</p>}
      {!loading && !error && data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
            {statCards.map(c => (
              <div
                key={c.label}
                style={{ ...cardStyle, cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => openDetails(c.type)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetails(c.type);
                  }
                }}
                title="点击查看列表"
              >
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{c.label}</span>
                <strong style={{ fontSize: '1.6rem' }}>{c.value}</strong>
              </div>
            ))}
          </div>

          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
            <h2 style={{ fontSize: '1.2rem', margin: '0 0 1rem 0' }}>最近教育记录</h2>
            {data.recent.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>暂无教育记录。</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--bg-secondary)' }}>
                      <th style={{ padding: '0.5rem', fontWeight: 600 }}>标题</th>
                      <th style={{ padding: '0.5rem', fontWeight: 600 }}>类型</th>
                      <th style={{ padding: '0.5rem', fontWeight: 600 }}>员工</th>
                      <th style={{ padding: '0.5rem', fontWeight: 600 }}>日期</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map(r => (
                      <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.5rem' }}>
                          {r.file_url ? (
                            <button
                              type="button"
                              onClick={() => openPdfPreview(r)}
                              style={{
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                color: 'var(--primary-color)',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                            >
                              {r.title || '查看附件'}
                            </button>
                          ) : (
                            r.title || '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem' }}>{r.education_type}</td>
                        <td style={{ padding: '0.5rem' }}>{r.employee_name}</td>
                        <td style={{ padding: '0.5rem' }}>{new Date(r.education_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {previewRecord?.file_url && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setPreviewRecord(null);
            }
          }}
        >
          <div
            style={{
              width: '82vw',
              maxWidth: 1080,
              height: '82vh',
              background: '#fff',
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 45px rgba(15,23,42,0.25)'
            }}
          >
            <div
              style={{
                padding: '0.75rem 1rem',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ fontWeight: 600 }}>{previewRecord.title || 'PDF 预览'}</div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => openWithAuth(previewRecord.file_url)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--primary-color)'
                  }}
                >
                  新窗口打开
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewRecord(null)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: 20,
                    padding: '0.4rem 0.95rem',
                    cursor: 'pointer'
                  }}
                >
                  关闭
                </button>
              </div>
            </div>
            <div style={{ flex: '1 1 auto', padding: '0.75rem' }}>
              <SimplePdfViewer url={previewRecord.file_url} fileName={previewRecord.title || ''} />
            </div>
          </div>
        </div>
      )}

      {detailOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 998
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetailOpen(false);
          }}
        >
          <div
            style={{
              width: '90vw',
              maxWidth: 1180,
              height: '82vh',
              background: '#fff',
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 45px rgba(15,23,42,0.25)'
            }}
          >
            <div
              style={{
                padding: '0.75rem 1rem',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ fontWeight: 700 }}>{detailTitle}</div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => fetchDetails(detailType)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: 20,
                    padding: '0.4rem 0.95rem',
                    cursor: 'pointer'
                  }}
                >
                  刷新
                </button>
                <button
                  type="button"
                  onClick={() => setDetailOpen(false)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: 20,
                    padding: '0.4rem 0.95rem',
                    cursor: 'pointer'
                  }}
                >
                  关闭
                </button>
              </div>
            </div>

            <div style={{ padding: '0.75rem 1rem', overflow: 'auto' }}>
              {detailLoading && <p style={{ margin: 0, color: 'var(--text-secondary)' }}>正在加载...</p>}
              {!detailLoading && detailRows.length === 0 && (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>暂无数据</p>
              )}

              {!detailLoading && detailRows.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', background: 'var(--bg-secondary)' }}>
                        {detailType === 'employees' ? (
                          <>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>姓名</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>部门</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>岗位</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>状态</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>操作</th>
                          </>
                        ) : (
                          <>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>员工</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>类型</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>文件</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>时间</th>
                            <th style={{ padding: '0.55rem', fontWeight: 700 }}>预览</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {detailRows.map((r) => (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                          {detailType === 'employees' ? (
                            <>
                              <td style={{ padding: '0.55rem' }}>{r.name || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.department || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.position || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.status || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/employee-files?employeeId=${encodeURIComponent(r.id)}`)}
                                  style={{
                                    border: '1px solid var(--border-color)',
                                    background: '#fff',
                                    borderRadius: 8,
                                    padding: '0.35rem 0.65rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  查看安全档案
                                </button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '0.55rem' }}>{r.employee_name || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.education_type || r.archive_type || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.file_name || '-'}</td>
                              <td style={{ padding: '0.55rem' }}>{r.created_at ? new Date(r.created_at).toLocaleString('zh-CN', { hour12: false }) : '-'}</td>
                              <td style={{ padding: '0.55rem' }}>
                                {r.file_url ? (
                                  <button
                                    type="button"
                                    onClick={() => openPdfPreview({
                                      title: r.file_name || 'PDF 预览',
                                      file_url: r.file_url
                                    })}
                                    style={{
                                      border: '1px solid var(--border-color)',
                                      background: '#fff',
                                      color: 'var(--primary-color)',
                                      borderRadius: 8,
                                      minWidth: 64,
                                      padding: '0.35rem 0.65rem',
                                      fontSize: '0.85rem',
                                      fontWeight: 700,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    预览
                                  </button>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary)' }}>无附件</span>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeOverviewPage;
